// CC-02 bloque B4 (MG-ILE-1074) · Administración › Maestro de productos › «Registros por autoridad».
//
// Spec `05_analisis_tecnico/Farmacovigilancia/ui/MG-ILE-57.md`, sección «Registros por autoridad»
// (AT S3 §11.3.2, RN-73, CC2-29-C6). La segunda manera de cargar los registros por autoridad, sin
// volver a subir el Excel (decisión del operador del 5-oct-2026):
//
//   · Se elige el país y se ven sus productos con los registros vigentes por autoridad (el
//     endpoint 46, `registros[]`). El filtro por nombre o SKU actúa sobre lo ya cargado.
//   · «Ver ficha» abre la ficha (`GET /administracion/maestro-de-productos/{pais}/{sku}`): una
//     casilla por autoridad activa del país, con su número de registro.
//   · Marcar exige el número; desmarcar desactiva el registro (no lo borra). Cada casilla que
//     cambia es UNA petición al endpoint 70; el servidor deja el asiento.
//
// Lo que decide el servidor no se repite aquí: qué autoridades se ofrecen, cuál es la
// predeterminada, si una está «por confirmar con ILENDER» o desactivada. La pantalla solo pide el
// número antes de enviar, con el mismo texto que respondería el servidor (CC2-29-C6).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Eye, Landmark, PackageSearch, RotateCcw, Search } from 'lucide-react';
import {
  Alert, Button, Card, Checkbox, DataTable, EmptyState, Field, Input, Mono, Modal, Select,
} from './ui';
import type { ColumnaDeTabla, FilaDeTabla } from './ui';
import { listarMaestroDeSku } from '../api/catalogos';
import type { ProductoDelMaestro } from '../api/catalogos';
import { cambiarRegistroAnteAutoridad, fichaDelProducto } from '../api/maestro';
import type { AutoridadDeLaFicha, FichaDelProducto } from '../api/maestro';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { useDiferido } from '../lib/useDiferido';

/** Las filas que se pintan a la vez: un país puede traer miles (HU-29-C10). */
const FILAS_A_LA_VEZ = 50;

export type PaisDeLaLista = { cod: string; nombre: string };

/** El testid de una autoridad: su código en minúsculas y kebab-case (`SENASA_CR` -> `senasa-cr`). */
export const sufijoDeAutoridad = (codigo: string) => codigo.toLowerCase().replaceAll('_', '-');

/** «Escribe el número de registro ante SANIPES» (CC2-29-C6), el mismo texto del servidor. */
export const faltaElNumero = (nombre: string) => `Escribe el número de registro ante ${nombre}`;

const comparable = (texto: string | null | undefined) =>
  (texto ?? '').normalize('NFD').replaceAll(/[̀-ͯ]/g, '').toLowerCase().trim();

const COLUMNAS: ColumnaDeTabla[] = [
  { key: 'sku', label: 'SKU', className: 'w-36' },
  { key: 'nombre_comercial', label: 'Nombre comercial' },
  { key: 'registros', label: 'Registros por autoridad', testid: 'maestro-productos-fila-registros' },
  { key: 'acciones', label: <span className="sr-only">Acciones</span>, className: 'w-36' },
];

type Casilla = { marcada: boolean; numero: string };

const casillasDe = (ficha: FichaDelProducto): Record<string, Casilla> =>
  Object.fromEntries(ficha.autoridades.map((a) => [a.codigo, { marcada: a.registrado, numero: a.registro_sanitario ?? '' }]));

/** Lo que cambió respecto de la ficha: una petición por autoridad. */
function cambiosDe(ficha: FichaDelProducto, casillas: Record<string, Casilla>) {
  return ficha.autoridades.flatMap((a) => {
    const c = casillas[a.codigo];
    if (!c) return [];
    if (c.marcada && (!a.registrado || c.numero.trim() !== (a.registro_sanitario ?? ''))) {
      return [{ autoridad: a, registrado: true, numero: c.numero.trim() }];
    }
    if (!c.marcada && a.registrado) return [{ autoridad: a, registrado: false, numero: '' }];
    return [];
  });
}

/** Lo que se dice bajo el nombre de la autoridad: predeterminada, por confirmar, desactivada. */
function pistaDe(a: AutoridadDeLaFicha): string | undefined {
  const partes = [
    a.es_predeterminada ? 'Predeterminada del país' : null,
    a.por_confirmar ? 'Por confirmar con ILENDER' : null,
    a.activa ? null : 'Desactivada: solo se puede desmarcar',
  ].filter(Boolean);
  return partes.length ? partes.join(' · ') : undefined;
}

function FichaDelProductoModal({ pais, sku, onCerrar, onGuardada }: {
  pais: string; sku: string; onCerrar: () => void; onGuardada: (nombre: string) => void;
}) {
  const [ficha, setFicha] = useState<FichaDelProducto | null>(null);
  const [casillas, setCasillas] = useState<Record<string, Casilla>>({});
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [falloDeCarga, setFalloDeCarga] = useState<Fallo | null>(null);
  const [falloDeGuardado, setFalloDeGuardado] = useState<Fallo | null>(null);
  const [guardando, setGuardando] = useState(false);
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => { vivo.current = false; };
  }, []);

  const abrir = useCallback(async () => {
    setFalloDeCarga(null);
    try {
      const leida = await fichaDelProducto(pais, sku);
      if (!vivo.current) return;
      setFicha(leida);
      setCasillas(casillasDe(leida));
    } catch (e) {
      if (vivo.current) setFalloDeCarga(aFallo(e));
    }
  }, [pais, sku]);

  useEffect(() => { abrir(); }, [abrir]);

  const cambios = ficha ? cambiosDe(ficha, casillas) : [];
  const sucio = cambios.length > 0;

  const cambiar = (codigo: string, cambio: Partial<Casilla>) => {
    setCasillas((c) => ({ ...c, [codigo]: { ...c[codigo], ...cambio } }));
    setErrores((e) => { const { [codigo]: _, ...resto } = e; return resto; });
  };

  const guardar = async () => {
    if (!ficha) return;
    const faltan = Object.fromEntries(cambios
      .filter((c) => c.registrado && !c.numero)
      .map((c) => [c.autoridad.codigo, faltaElNumero(c.autoridad.nombre)]));
    setErrores(faltan);
    setFalloDeGuardado(null);
    if (Object.keys(faltan).length) return;
    setGuardando(true);
    let ultima = ficha;
    try {
      for (const c of cambios) {
        ultima = await cambiarRegistroAnteAutoridad(pais, sku, c.autoridad.codigo, {
          registrado: c.registrado, registro_sanitario: c.registrado ? c.numero : null,
        });
      }
      if (vivo.current) onGuardada(ultima.nombre_comercial);
    } catch (e) {
      if (!vivo.current) return;
      setFalloDeGuardado(aFallo(e));
      // Lo que alcanzó a guardarse ya está en el servidor: la ficha se vuelve a leer de ahí.
      try {
        const leida = await fichaDelProducto(pais, sku);
        if (vivo.current) { setFicha(leida); setCasillas(casillasDe(leida)); }
      } catch { /* el aviso de arriba ya dice que no se guardó */ }
    } finally {
      if (vivo.current) setGuardando(false);
    }
  };

  const pie = (
    <>
      <Button variant="secondary" onClick={onCerrar} disabled={guardando} data-testid="maestro-ficha-cancelar">Cancelar</Button>
      <Button onClick={() => { guardar(); }} disabled={!ficha || !sucio || guardando} data-testid="maestro-ficha-guardar">
        {guardando ? 'Guardando…' : 'Guardar cambios'}
      </Button>
    </>
  );

  return (
    <Modal open onClose={onCerrar} ancho="lg" sucio={sucio} data-testid="maestro-ficha" icon={Landmark}
      eyebrow="Administración · Maestro de productos" footer={pie}
      title={ficha ? `${ficha.nombre_comercial} · ${ficha.sku}` : `Ficha del producto ${sku}`}>
      {!ficha && !falloDeCarga && (
        <p role="status" className="text-body text-ink-soft" data-testid="maestro-ficha-cargando">Cargando la ficha…</p>
      )}
      {falloDeCarga && (
        <div className="space-y-3">
          <Alert tone="danger" title="No se pudo abrir la ficha" data-testid="maestro-ficha-error" detalleTecnico={falloDeCarga.detalleTecnico}>
            {falloDeCarga.message}{falloDeCarga.estado === 0 ? ' Revisa tu conexión e intenta refrescar.' : ''}
          </Alert>
          <Button variant="secondary" icon={RotateCcw} onClick={() => { abrir(); }}>Reintentar</Button>
        </div>
      )}
      {ficha && (
        <div className="space-y-4">
          <p className="text-body text-ink-soft">
            {ficha.pais_nombre}
            {ficha.forma_farmaceutica ? ` · ${ficha.forma_farmaceutica}` : ''}
            {ficha.concentracion ? ` · ${ficha.concentracion}` : ''}
          </p>
          <fieldset className="space-y-3" data-testid="maestro-ficha-autoridades">
            <legend className="font-semibold text-meta text-ink-soft mb-2">
              Autoridades del país ante las que está registrado
            </legend>
            {ficha.autoridades.length === 0 && (
              <p className="text-body text-ink-soft">Este país todavía no tiene autoridades configuradas.</p>
            )}
            {ficha.autoridades.map((a) => {
              const c = casillas[a.codigo] ?? { marcada: false, numero: '' };
              const sufijo = sufijoDeAutoridad(a.codigo);
              return (
                <div key={a.codigo} className="space-y-2">
                  <Checkbox checked={c.marcada} label={a.nombre} hint={pistaDe(a)}
                    disabled={guardando || (!a.activa && !c.marcada)}
                    data-testid={`maestro-ficha-autoridad-${sufijo}`}
                    onChange={(e) => cambiar(a.codigo, { marcada: e.target.checked })} />
                  {c.marcada && (
                    <div className="pl-8">
                      <Field label={`N.º de registro ante ${a.nombre}`} required error={errores[a.codigo]}>
                        <Input value={c.numero} maxLength={100} disabled={guardando || !a.activa}
                          data-testid={`maestro-ficha-registro-${sufijo}`}
                          onChange={(e) => cambiar(a.codigo, { numero: e.target.value })} />
                      </Field>
                    </div>
                  )}
                </div>
              );
            })}
          </fieldset>
          <p className="text-meta text-ink-soft">
            Desmarcar una autoridad deja el registro inactivo: no se borra y los casos ya registrados no cambian.
          </p>
          {falloDeGuardado && (
            <Alert tone="danger" title="No se guardaron todos los cambios" data-testid="maestro-ficha-error-envio"
              detalleTecnico={falloDeGuardado.detalleTecnico}>
              {falloDeGuardado.message}
            </Alert>
          )}
        </div>
      )}
    </Modal>
  );
}

export default function ProductosDelPais({ paises, version = 0 }: {
  paises: PaisDeLaLista[];
  /** Cambia tras cada carga del archivo: la lista se vuelve a pedir. */
  version?: number;
}) {
  const [pais, setPais] = useState('');
  const [productos, setProductos] = useState<ProductoDelMaestro[] | null>(null);
  const [cargando, setCargando] = useState(false);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const buscado = comparable(useDiferido(busqueda));
  const [abierta, setAbierta] = useState<string | null>(null);
  const [guardada, setGuardada] = useState<string | null>(null);
  const pedido = useRef('');

  const cargar = useCallback(async (codigo: string) => {
    pedido.current = codigo;
    setFallo(null);
    if (!codigo) { setProductos(null); return; }
    setCargando(true);
    try {
      const respuesta = await listarMaestroDeSku(codigo);
      // Las semillas «dato de prueba» no tienen ficha en el país: no hay registros que tocar.
      if (pedido.current === codigo) setProductos((respuesta?.productos ?? []).filter((p) => !p.es_dato_de_prueba));
    } catch (e) {
      if (pedido.current === codigo) setFallo(aFallo(e));
    } finally {
      if (pedido.current === codigo) setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(pais); }, [pais, version, cargar]);

  const coincidentes = useMemo(
    () => (productos ?? []).filter((p) => !buscado || comparable(p.nombre_comercial).includes(buscado) || comparable(p.sku).includes(buscado)),
    [productos, buscado],
  );
  const visibles = coincidentes.slice(0, FILAS_A_LA_VEZ);

  const celda = (fila: FilaDeTabla, { key }: ColumnaDeTabla): ReactNode => {
    const p = fila as unknown as ProductoDelMaestro;
    if (key === 'sku') return <Mono>{p.sku}</Mono>;
    if (key === 'nombre_comercial') return p.nombre_comercial;
    if (key === 'registros') {
      const registros = p.registros ?? [];
      if (!registros.length) return <span className="text-ink-soft">Sin registro por autoridad</span>;
      return (
        <ul className="space-y-0.5">
          {registros.map((r) => (
            <li key={r.autoridad_codigo}>{r.autoridad_nombre} · <Mono>{r.registro_sanitario}</Mono></li>
          ))}
        </ul>
      );
    }
    return (
      <Button size="sm" variant="ghost" icon={Eye} data-testid="maestro-productos-fila-ver"
        aria-label={`Ver la ficha de ${p.nombre_comercial} (${p.sku})`} onClick={() => { setGuardada(null); setAbierta(p.sku); }}>
        Ver ficha
      </Button>
    );
  };

  const opciones = paises.map((p) => ({ value: p.cod, label: p.nombre }));

  return (
    <Card title="Registros por autoridad" icon={Landmark} className="mb-4" pad={false} data-testid="maestro-productos-tarjeta"
      subtitle="Busca un producto del país y abre su ficha para marcar o desmarcar las autoridades ante las que está registrado, sin volver a cargar el archivo.">
      <div className="flex flex-wrap items-end gap-3 px-4 py-3 border-b border-hairline">
        <div className="w-full sm:w-60">
          <Field label="País">
            <Select data-testid="maestro-productos-pais" placeholder="Elige el país..." sinVacio buscable
              value={pais} options={opciones} onChange={(e) => { setBusqueda(''); setPais(String(e.target.value ?? '')); }} />
          </Field>
        </div>
        <div className="relative flex-1 basis-full sm:basis-auto sm:min-w-[13.75rem]">
          <Search size={15} aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none" />
          <Input className="pl-9" aria-label="Buscar producto por nombre comercial o SKU" placeholder="Nombre comercial o SKU…"
            value={busqueda} disabled={!pais} data-testid="maestro-productos-buscar" onChange={(e) => setBusqueda(e.target.value)} />
        </div>
      </div>

      {guardada && (
        <div className="px-4 pt-4">
          <Alert tone="success" title="Ficha guardada" onCerrar={() => setGuardada(null)} data-testid="maestro-ficha-exito">
            {`Se guardaron los registros por autoridad de ${guardada}.`}
          </Alert>
        </div>
      )}

      {!pais && (
        <EmptyState icon={PackageSearch} title="Elige un país para ver sus productos." data-testid="maestro-productos-sin-pais" />
      )}
      {pais && cargando && productos === null && (
        <p role="status" className="p-5 text-body text-ink-soft" data-testid="maestro-productos-cargando">Cargando los productos…</p>
      )}
      {pais && fallo && (
        <div className="p-5 space-y-3">
          <Alert tone="danger" title="No se pudieron cargar los productos" data-testid="maestro-productos-error" detalleTecnico={fallo.detalleTecnico}>
            {fallo.message}{fallo.estado === 0 ? ' Revisa tu conexión e intenta refrescar.' : ''}
          </Alert>
          <Button variant="secondary" icon={RotateCcw} onClick={() => { cargar(pais); }}>Reintentar</Button>
        </div>
      )}
      {pais && !fallo && productos !== null && (
        <>
          {coincidentes.length > FILAS_A_LA_VEZ && (
            <p className="px-4 pt-3 text-meta text-ink-soft" data-testid="maestro-productos-acotar">
              {`Mostrando ${FILAS_A_LA_VEZ} de ${coincidentes.length} productos. Escribe parte del nombre o del SKU para acotar.`}
            </p>
          )}
          <DataTable sinMarco columns={COLUMNAS} data-testid="maestro-productos" testidDeFila="maestro-productos-fila"
            regionDesplazable="Productos del país" renderCell={celda}
            rows={visibles.map((p) => ({ ...p, id: p.sku }))}
            empty={productos.length === 0 ? (
              <EmptyState icon={PackageSearch} data-testid="maestro-productos-vacio"
                title="Este país todavía no tiene productos cargados."
                description="Carga el maestro con el archivo: sus productos aparecerán aquí." />
            ) : (
              <EmptyState icon={Search} data-testid="maestro-productos-sin-resultados"
                title="Ningún producto coincide con la búsqueda." description="Prueba con otra parte del nombre o del SKU." />
            )} />
        </>
      )}

      {abierta && pais && (
        <FichaDelProductoModal pais={pais} sku={abierta} onCerrar={() => setAbierta(null)}
          onGuardada={(nombre) => { setAbierta(null); setGuardada(nombre); cargar(pais); }} />
      )}
    </Card>
  );
}
